// La fila de un agente en la lista (y su retrato con insignia, que usa también el riel). Entrega 3 (04-11): UNA acción
// principal por fila urgente, DENTRO de la fila a la derecha (flujo.md #10): «Revisar» (una subida suya espera tu Allow),
// «Sí» (subir a pruebas: el permiso sencillo del servidor) o «Cerrar» (ha terminado). Las preguntas no llevan botón: se
// contestan escribiendo. Clic derecho (o dedo mantenido) abre su menú: abrir, aparcar o despertar.
import * as MenuContexto from '@radix-ui/react-context-menu';
import { Clock, Handshake, MessageCircle, Moon, Sun, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useToastStore } from '../../../stores/toast.store';
import { oficinaApi } from '../api';
import { useFaseCierre, cerrarConDeshacer } from '../cierre/cerrarConDeshacer';
import type { ContratadoDTO, Situacion } from '../compartido/vocabulario';
import type { Despliegue } from '../despliegues/api';
import { useBandejaStore } from '../despliegues/store';
import { mensajeError } from '../errores';
import { useNoLeido } from '../leidos';
import { useOficinaStore } from '../store';
import { Boton, IconoEncargo } from '../ui';
import { usePasoEnVivo } from '../chat/paso-en-vivo';
import { hace, quien, quienConFrase, ultimoMovimiento } from './estado';
import { aparcadoDe, aspecto, aspectoDe, aspectoEnGrupo, bloqueDe, esperaDesde, etiquetaSituacion, fraseAparcado, fraseDeFila, seVa, sinNoticiasDesde, type Aspecto, type Grupo, type SantiDelAgente } from './situacion';

/** El icono de la situación sobre su color (la misma insignia en la lista, el chat y el riel). */
export function InsigniaSituacion({ situacion, c, a: forzado, tamano = 18, className }: { situacion: Situacion; c?: Pick<ContratadoDTO, 'situacion' | 'pide'>; a?: Aspecto; tamano?: number; className?: string }) {
  const a = forzado ?? (c ? aspectoDe(c) : aspecto(situacion));
  const Icono = a.icono;
  // 7 · sin nada pendiente: sin insignia (apagado).
  if (!Icono) return null;
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full', a.insignia, className)} style={{ width: tamano, height: tamano }} aria-hidden>
      <Icono style={{ width: Math.round(tamano * 0.62), height: Math.round(tamano * 0.62) }} strokeWidth={2.5} />
    </span>
  );
}

/** Retrato con la insignia de su situación abajo a la derecha (nunca solo color). El aparcado duerme: sin insignia. */
export function AvatarSituacion({ c, a, tamano = 44, anillo = 'ring-oficina-panel', dormido = false }: { c: ContratadoDTO; a?: Aspecto; tamano?: number; anillo?: string; dormido?: boolean }) {
  return (
    <span className={cn('relative inline-flex shrink-0', dormido && 'opacity-60')}>
      <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={tamano} />
      {dormido ? null : <InsigniaSituacion situacion={c.situacion} c={c} a={a} tamano={Math.max(20, Math.round(tamano * 0.46))} className={cn('absolute -bottom-0.5 -right-1 ring-2', anillo)} />}
    </span>
  );
}

export interface PropsFila {
  c: ContratadoDTO;
  grupo: Grupo;
  santi?: SantiDelAgente;
  /** Su subida pendiente (solo en el grupo «subida»). */
  subida?: Despliegue;
  /** La hora con la que se mira (useEquipo): la misma para la fila y su bloque. */
  ahora: number;
  activo: boolean;
  alAbrir: (id: string) => void;
  alResaltar?: (id: string | null) => void;
}

export function FilaEquipo({ c, grupo, santi, subida, ahora, activo, alAbrir, alResaltar }: PropsFila) {
  const a = aspectoEnGrupo(c, grupo);
  const dormido = grupo === 'aparcado' ? aparcadoDe(c, santi, { subida: subida ?? null, ahora }) : null;
  // 29-10: lo que se va no hace ruido; lo aparcado tampoco (sin punto de no leído).
  const noLeido = useNoLeido(c) && !seVa(c) && !dormido;
  const urgente = bloqueDe(grupo) === 'te_toca';
  const terminado = grupo === 'terminado';
  // Como un chat: lo que no ha leído = negrita + punto.
  const sinLeer = noLeido && !activo;
  // «hace X» siempre: lo que te espera, desde cuándo espera; el resto, su último movimiento.
  const cuando = hace(subida ? subida.creadoEn : urgente || terminado ? esperaDesde(c) : ultimoMovimiento(c));
  // 09-10 — trabaja pero lleva rato callado: en vez de la hora, «sin noticias · 25 min» en ámbar. Con una subida
  // esperando, lo que cuenta es cuánto lleva esperando la subida.
  const sinNoticias = subida ? null : sinNoticiasDesde(c);
  // Entrega 4 (flujo.md #7) — mientras trabaja, lo que hace AHORA en vez de su titular (chat/paso-en-vivo.ts; como
  // mucho un repintado por segundo). En la fila, con el orden de todas las filas (la hora delante, como Mail):
  // «Oficina · hace 20 s · Paso 7 · Comprobando los precios», hasta 2 líneas; en la lista estrecha, la hora al final se
  // cortaba. La hora es la de ESE paso: si lleva 15 min en él, en ámbar como «sin noticias» (que sustituye).
  const vivo = usePasoEnVivo(c.agentId, c.situacion === 'trabajando' && !subida && !dormido);
  const frase = subida ? t('oficinaV2.lista.subida.quiere', { que: subida.paraPau.titulo }) : vivo ? [vivo.paso, vivo.texto].filter(Boolean).join('. ') : fraseDeFila(c);
  const etiqueta = subida ? t('oficinaV2.lista.subida.etiqueta') : dormido ? fraseAparcado(dormido, ahora) : etiquetaSituacion(c.situacion);
  // La sala solo si hay más de una (con una sola, sobra).
  const sala = useOficinaStore((s) => (s.salas.length > 1 ? s.salas.find((x) => x.projectId === c.projectId) : undefined));
  // 04-11 (mesas de equipo, grupos.md §5): su grupo delante de la frase, apagado («Precios · Subir a pruebas…»), como el
  // buzón que Mail enseña en la bandeja unificada. Sin grupo, nada. El grupo nunca cambia el orden ni el bloque.
  const grupoMesa = useOficinaStore((s) => (c.grupoId ? s.grupos.find((g) => g.id === c.grupoId)?.nombre : undefined));
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (activo) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [activo]);
  // La acción de la fila (una, la que decide): la subida, el permiso sencillo del servidor o cerrar lo terminado.
  const rapido = grupo === 'autorizacion' && c.permisoRapido ? c.permisoRapido : null;
  const accion: 'revisar' | 'si' | 'cerrar' | null = subida ? 'revisar' : rapido ? 'si' : terminado ? 'cerrar' : null;
  return (
    <MenuFila c={c} dormido={!!dormido} alAbrir={alAbrir}>
      <li className="relative" data-grupo={grupo}>
        <button
          ref={ref}
          type="button"
          onClick={() => alAbrir(c.agentId)}
          // Solo con ratón (06-10, Pau en el iPad): al tocar, el iPad deja un «ratón» de mentira encima de la fila
          // y la tarjeta del mundo se quedaba fija sobre el chat recién abierto. Igual que las mesas del mundo.
          onPointerEnter={(e) => e.pointerType === 'mouse' && alResaltar?.(c.agentId)}
          onPointerLeave={(e) => e.pointerType === 'mouse' && alResaltar?.(null)}
          aria-current={activo ? 'true' : undefined}
          aria-label={`${quien(c)}${sala ? ` (${sala.nombre})` : ''}${grupoMesa ? `, ${t('oficinaV2.grupos.enGrupo', { nombre: grupoMesa })}` : ''}. ${etiqueta}. ${frase}${sinNoticias && !vivo ? `. ${sinNoticias}` : ''}`}
          title={quienConFrase(c)}
          className={cn(
            'flex w-full min-w-0 items-start gap-3 rounded-o-m py-2 pl-2.5 pr-2 text-left outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
            // 04-11 (Entrega 3, maqueta lista-despues): lo que te toca va en una tarjeta gris, como una notificación; el
            // color lo llevan solo la insignia y el botón (antes la fila entera se teñía de oro o violeta: ~14 zonas
            // doradas en reposo y el oro dejaba de decir «actúa»). La seleccionada, en el gris de selección de macOS.
            activo ? 'bg-oficina-pulsado' : urgente ? 'bg-white/[0.05] hover:bg-white/[0.08]' : 'hover:bg-white/[0.05]',
          )}
        >
          <AvatarSituacion c={c} a={a} tamano={40} anillo={activo ? 'ring-oficina-pulsado' : 'ring-oficina-elevada'} dormido={!!dormido} />
          {/* La acción va DENTRO de la fila (a la derecha, encima de su fondo): la fila ya no se encoge entera (antes el
              «Sí» iba fuera y la fila teñida se cortaba antes que las demás); solo el texto le deja su hueco. */}
          <span className={cn('min-w-0 flex-1', accion === 'si' ? 'pr-14' : accion ? 'pr-20' : null)}>
            {/* 10-10 (Pau aprobó «el título entero»): el título usa TODO su ancho, hasta 2 líneas; la hora va abajo. */}
            <span className="flex items-start gap-2">
              <span data-testid="fila-titulo" className={cn('line-clamp-2 min-w-0 flex-1 break-words text-o-cuerpo leading-5', dormido ? 'text-oficina-texto2' : 'text-oficina-texto', sinLeer || urgente ? 'font-semibold' : 'font-medium')}>{quien(c)}</span>
              {sinLeer ? <span className="mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full bg-oficina-texto" aria-label={t('oficinaV2.s12.sinLeer')} /> : null}
            </span>
            <span
              className={cn(
                'mt-0.5 block text-o-sub leading-[18px]',
                // Lo que te para: lo que pide es el motivo para pulsar → hasta 2 líneas. El resto, 1.
                // Lo que te para (permiso, subida, no puede seguir): lo que pide es el motivo para pulsar → hasta 2 líneas.
                a.fila || vivo ? 'line-clamp-2' : 'line-clamp-1',
                sinLeer ? 'font-semibold text-oficina-texto' : 'text-oficina-texto2',
              )}
            >
              {/* Delante de la frase, apagado: sala (si hay varias) · hora (o desde cuándo duerme) · Santi. */}
              <span className="font-normal text-oficina-texto2" data-testid="fila-meta">
                {sala ? (
                  <span data-testid="sala-fila">
                    <span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: sala.color }} aria-hidden />
                    {sala.nombre} ·{' '}
                  </span>
                ) : null}
                {vivo ? (
                  <span className={cn('text-o-nota tabular-nums', vivo.largo && 'text-oficina-bloqueado')} data-testid="paso-hace">
                    {vivo.largo ? <Clock className="mr-1 inline h-3 w-3 align-[-1px]" strokeWidth={2.5} aria-hidden /> : null}
                    {vivo.hace}
                  </span>
                ) : sinNoticias ? (
                  <span className="text-o-nota tabular-nums text-oficina-bloqueado" data-testid="sin-noticias">
                    <Clock className="mr-1 inline h-3 w-3 align-[-1px]" strokeWidth={2.5} aria-hidden />
                    {sinNoticias}
                  </span>
                ) : dormido ? (
                  <span className={cn('text-o-nota', dormido.proponerCerrar && 'font-semibold text-oficina-bloqueado')} data-testid="fila-aparcado">
                    {etiqueta}
                  </span>
                ) : (
                  <span className={cn('text-o-nota tabular-nums', sinLeer && 'font-semibold text-oficina-texto')} data-testid="fila-hora">{cuando}</span>
                )}
                {santi ? <Handshake className="ml-1 inline h-3.5 w-3.5 align-[-2px] text-oficina-santi" strokeWidth={2.2} aria-label={t('oficinaV2.s12.conSanti.marca')} data-testid="fila-santi" /> : null}
                {' · '}
              </span>
              {grupoMesa ? (
                <span className="font-normal text-oficina-texto3" data-testid="fila-grupo">
                  {grupoMesa}
                  {' · '}
                </span>
              ) : null}
              {vivo ? (
                <span data-testid="paso-en-vivo">
                  {vivo.paso ? <span className={cn('font-medium', sinLeer ? null : 'text-oficina-texto')}>{vivo.paso} · </span> : null}
                  {vivo.texto}
                </span>
              ) : (
                frase
              )}
            </span>
          </span>
        </button>
        {/* Hermana de la fila, no dentro (un botón no puede ir dentro de otro): pulsarla no abre el chat. */}
        {accion ? (
          <span className="absolute right-2 top-1/2 -translate-y-1/2">
            {accion === 'revisar' && subida ? <BotonRevisar c={c} subida={subida} /> : accion === 'si' && rapido ? <SiRapido c={c} permisoId={rapido.id} /> : <BotonCerrar c={c} />}
          </span>
        ) : null}
      </li>
    </MenuFila>
  );
}

/** Las acciones de la fila: cápsulas de 32 px; la zona táctil llega a 44 (::before). */
const CAPSULA = "relative h-8 min-h-0 min-w-11 px-3 text-o-sub before:absolute before:-inset-1.5 before:content-['']";

/** «Revisar»: abre la ventana de su subida (la misma que la de la bandeja, con su Allow). Oro: es decidir. */
function BotonRevisar({ c, subida }: { c: ContratadoDTO; subida: Despliegue }) {
  const abrirSubida = useBandejaStore((s) => s.abrirSubida);
  return (
    <Boton variante="primario" onClick={() => abrirSubida(subida.runId)} aria-label={t('oficinaV2.lista.accion.revisarAria', { nombre: quien(c), que: subida.paraPau.titulo })} className={CAPSULA} data-testid="fila-revisar">
      {t('oficinaV2.lista.accion.revisar')}
    </Boton>
  );
}

/** «Cerrar»: sin diálogo, con 6 s para deshacer (cierre/cerrarConDeshacer.ts). Gris: el oro es para decidir. */
function BotonCerrar({ c }: { c: ContratadoDTO }) {
  const fase = useFaseCierre(c.agentId);
  return (
    <Boton cargando={fase !== null} onClick={() => cerrarConDeshacer([c])} aria-label={t('oficinaV2.lista.accion.cerrarAria', { nombre: quien(c) })} className={CAPSULA} data-testid="fila-cerrar">
      {t('oficinaV2.lista.accion.cerrar')}
    </Boton>
  );
}

/**
 * El «Sí» de la lista: aprueba el permiso sin abrir el chat (oro: es actuar). Mientras va, girando y quieto; si
 * falla, un aviso con el motivo. Al salir bien no hace falta nada más: el agente deja de pedir permiso y la fila se
 * va de «Te toca» sola (llega por el socket); hasta entonces el botón no vuelve, para no aprobar dos veces.
 */
export function SiRapido({ c, permisoId }: { c: ContratadoDTO; permisoId: string }) {
  const [estado, setEstado] = useState<{ id: string; fase: 'enviando' | 'hecho' } | null>(null);
  const actual = estado?.id === permisoId ? estado.fase : null;
  if (actual === 'hecho') return null;
  const aprobar = async () => {
    setEstado({ id: permisoId, fase: 'enviando' });
    try {
      await oficinaApi.responderPermiso(c.agentId, permisoId, 'aprobar');
      setEstado({ id: permisoId, fase: 'hecho' });
    } catch (e) {
      setEstado(null);
      useToastStore.getState().push({ message: t('oficinaV2.s12.rapido.error', { nombre: quien(c), motivo: mensajeError(e) }) });
    }
  };
  return (
    <Boton
      variante="primario"
      cargando={actual === 'enviando'}
      onClick={() => void aprobar()}
      aria-label={t('oficinaV2.s12.rapido.aria', { nombre: quien(c), que: fraseDeFila(c) })}
      title={t('oficinaV2.s12.rapido.pista')}
      className={CAPSULA}
      data-testid="si-rapido"
    >
      {t('oficinaV2.s12.rapido.si')}
    </Boton>
  );
}

/** Clic derecho (o dedo mantenido) sobre la fila: abrir su chat, aparcarlo o despertarlo. */
function MenuFila({ c, dormido, alAbrir, children }: { c: ContratadoDTO; dormido: boolean; alAbrir: (id: string) => void; children: React.ReactNode }) {
  const aparcar = useOficinaStore((s) => s.aparcar);
  // Lo que trabaja o se va no se aparca (no está quieto): el menú solo ofrece abrirlo.
  const quieto = !['trabajando', 'llegando', 'cerrando', 'saliendo'].includes(c.situacion);
  return (
    <MenuContexto.Root>
      <MenuContexto.Trigger asChild>{children}</MenuContexto.Trigger>
      <MenuContexto.Portal>
        <MenuContexto.Content
          aria-label={t('oficinaV2.escena.acciones', { nombre: quien(c) })}
          className="z-[70] min-w-[220px] rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95"
          data-testid="menu-fila"
        >
          <OpcionFila icono={MessageCircle} alElegir={() => alAbrir(c.agentId)}>{t('oficinaV2.escena.abrirChat')}</OpcionFila>
          {dormido ? (
            <OpcionFila icono={Sun} alElegir={() => void aparcar(c.agentId, false)}>{t('oficinaV2.lista.aparcado.despertar')}</OpcionFila>
          ) : quieto ? (
            <OpcionFila icono={Moon} alElegir={() => void aparcar(c.agentId, true)}>{t('oficinaV2.lista.aparcado.aparcar')}</OpcionFila>
          ) : null}
        </MenuContexto.Content>
      </MenuContexto.Portal>
    </MenuContexto.Root>
  );
}

function OpcionFila({ icono: Icono, alElegir, children }: { icono: LucideIcon; alElegir: () => void; children: string }) {
  return (
    <MenuContexto.Item
      onSelect={alElegir}
      className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-white/[0.06]"
    >
      <Icono className="h-4 w-4 text-oficina-texto2" aria-hidden />
      {children}
    </MenuContexto.Item>
  );
}
